import {
	CheckCircle,
	Copy,
	FileUp,
	Lock,
	QrCode,
	Settings,
	Trash2,
	Unlock,
	User,
} from "lucide-react";
import { useEffect, useState } from "react";
import StatusBadge from "@/components/_shared/StatusBadge";

interface RoomStatusLeisteProps {
	raumId: string;
	passwort?: string | null;
	nurBesitzerUpload: boolean;
	nurBesitzerLoeschen: boolean;
	istBesitzer: boolean;
	onVerlaengernClick: () => void;
	onLinkKopieren: () => void;
	onQrAnzeigen: () => void;
	onOpenSettings?: () => void;
	ablaufZeit?: number;
}

export function RoomStatusLeiste({
	raumId,
	passwort,
	nurBesitzerUpload,
	nurBesitzerLoeschen,
	istBesitzer,
	onVerlaengernClick,
	onLinkKopieren,
	onQrAnzeigen,
	onOpenSettings,
	ablaufZeit,
}: RoomStatusLeisteProps) {
	const [kopiert, setKopiert] = useState(false);
	const [ablaufFormatiert, setAblaufFormatiert] = useState<string>("");

	// Ablauf entfernt – Infos bleiben leer
	useEffect(() => {
		setAblaufFormatiert("");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleLinkKopieren = () => {
		setKopiert(true);
		onLinkKopieren();
		setTimeout(() => setKopiert(false), 2000);
	};

	const resetTooltipText = undefined;

	return (
		<div className="flex flex-col flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm sm:flex-row dark:border-gray-700 dark:bg-gray-800/70">
			{/* Left side: Room ID & Actions */}
			<div className="flex flex-wrap items-center gap-2">
				<StatusBadge
					className="px-3 py-1"
					color="gray"
					text={
						<>
							<span className="mr-1 font-medium text-gray-500 dark:text-gray-400">
								Raum:
							</span>
							<span className="font-mono text-blue-600 dark:text-blue-400">
								{raumId}
							</span>
						</>
					}
				/>
				<StatusBadge
					className="px-3 py-1"
					color="gray"
					icon={kopiert ? CheckCircle : Copy}
					iconClassName={kopiert ? "text-green-500" : ""}
					onClick={handleLinkKopieren}
					text="Link kopieren"
				/>
				<StatusBadge
					className="px-3 py-1"
					color="gray"
					icon={QrCode}
					onClick={onQrAnzeigen}
					text="QR-Code"
					tooltip="QR-Code für diesen Raum anzeigen"
				/>
			</div>

			{/* Right side: Status Badges + Settings (rechts außen) */}
			<div className="flex items-center gap-2">
				<RightStatusBadges
					ablaufFormatiert={ablaufFormatiert}
					istBesitzer={istBesitzer}
					nurBesitzerLoeschen={nurBesitzerLoeschen}
					nurBesitzerUpload={nurBesitzerUpload}
					onVerlaengernClick={onVerlaengernClick}
					passwort={passwort}
					resetTooltipText={resetTooltipText}
				/>
				{istBesitzer && (
					<StatusBadge
						className="px-3 py-1"
						color="gray"
						icon={Settings}
						onClick={onOpenSettings}
						text="Einstellungen"
						tooltip="Raumeinstellungen öffnen"
					/>
				)}
			</div>
		</div>
	);
}

type RightStatusBadgesProps = Pick<
	RoomStatusLeisteProps,
	| "passwort"
	| "nurBesitzerUpload"
	| "nurBesitzerLoeschen"
	| "istBesitzer"
	| "onVerlaengernClick"
> & { ablaufFormatiert: string; resetTooltipText?: string };

function RightStatusBadges({
	passwort,
	nurBesitzerUpload,
	nurBesitzerLoeschen,
}: RightStatusBadgesProps) {
	return (
		<div className="flex flex-wrap items-center gap-2">
			{/* Password Status */}
			<StatusBadge
				color={passwort ? "amber" : "gray"}
				icon={passwort ? Lock : Unlock}
				text={passwort ? "Geschützt" : "Offen"}
				tooltip={passwort ? "Raum ist passwortgeschützt" : "Kein Passwort"}
			/>

			{/* Upload Permission */}
			<StatusBadge
				color={nurBesitzerUpload ? "blue" : "green"}
				icon={nurBesitzerUpload ? User : FileUp}
				text={`Upload: ${nurBesitzerUpload ? "Besitzer" : "Anonym/Eingeloggt"}`}
				tooltip={
					nurBesitzerUpload
						? "Nur der Besitzer darf hochladen"
						: "Anonyme oder eingeloggte Nutzer dürfen hochladen"
				}
			/>

			{/* Delete Permission */}
			<StatusBadge
				color={nurBesitzerLoeschen ? "blue" : "green"}
				icon={nurBesitzerLoeschen ? User : Trash2}
				text={`Löschen: ${nurBesitzerLoeschen ? "Besitzer" : "Uploader/Bes."}`}
				tooltip={
					nurBesitzerLoeschen
						? "Nur der Besitzer darf löschen"
						: "Besitzer oder Uploader dürfen löschen"
				}
			/>

			{/* Ablauf entfernt */}
		</div>
	);
}
